/**
 * @file audioAssetSource.js
 * @description Resolves logical audio asset identity and adds a lightweight
 *              browser-side cache for prebuffer playback.
 *
 * This file decides WHAT is cached and under which key; `audioCacheStore.js` owns the store
 * itself and how long anything stays in it.
 */

import { resolveApiBase } from '../../api/httpClient.js';
import { getRoomGrantHeaders, getRoomGrantId } from '../../room/roomGrant.js';
import {
  currentAudioCacheGeneration,
  noteAudioCachePlay,
  openAudioBucket,
  writeAudioCacheEntry,
} from './audioCacheStore.js';
// The key scheme moved out whole when a download needed to name a waveform and a texture as well
// as audio. It is imported rather than restated so a downloaded track pins THIS entry — one name
// for one set of bytes, whether they arrived by playing or by being asked for.
import { toCacheRequestUrl, trackAudioKey } from '../../offline/offlineKeys.js';
import { downloadedTrackVariant } from '../../offline/offlineAvailability.js';
import { getLocalByteSource } from '../../local/localByteSources.js';

const AUDIO_PROXY_UNAVAILABLE_PREFIX = 'orbiters:audio-proxy-unavailable:';
const AUDIO_PROXY_UNAVAILABLE_TTL_MS = 5 * 60 * 1000;

function nowMs() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function normalizeAssetFormatPreference(value) {
  return String(value || '').toLowerCase() === 'pcm' ? 'pcm' : 'mp3';
}

function normalizeId(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

function buildLogicalCacheIdentity({ trackId, releaseVersion, variant, assetFormat }) {
  return trackAudioKey({ trackId, version: releaseVersion, variant, assetFormat });
}

function getAudioProxyUnavailableStorageKey(stableUrl) {
  return `${AUDIO_PROXY_UNAVAILABLE_PREFIX}${stableUrl}`;
}

function readStableAudioUrlUnavailableRecord(stableUrl) {
  if (!stableUrl || typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getAudioProxyUnavailableStorageKey(stableUrl));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const referenceTime = Number(parsed?.disabledAt ?? parsed?.lastFailureAt);
    if (!Number.isFinite(referenceTime) || referenceTime <= 0) {
      window.localStorage.removeItem(getAudioProxyUnavailableStorageKey(stableUrl));
      return null;
    }
    if ((Date.now() - referenceTime) > AUDIO_PROXY_UNAVAILABLE_TTL_MS) {
      window.localStorage.removeItem(getAudioProxyUnavailableStorageKey(stableUrl));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function isStableAudioUrlTemporarilyUnavailable(stableUrl) {
  const record = readStableAudioUrlUnavailableRecord(stableUrl);
  return Boolean(record?.disabledAt);
}

function writeStableAudioUrlRecord(stableUrl, payload) {
  if (!stableUrl || typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(
      getAudioProxyUnavailableStorageKey(stableUrl),
      JSON.stringify(payload),
    );
  } catch {
    // Ignore storage write failures.
  }
}

function clearStableAudioUrlTemporarilyUnavailable(stableUrl) {
  if (!stableUrl || typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.removeItem(getAudioProxyUnavailableStorageKey(stableUrl));
  } catch {
    // Ignore storage write failures.
  }
}

function resolveVariantCandidate(trackData, assetFormatPreference = 'mp3') {
  const normalizedPreference = normalizeAssetFormatPreference(assetFormatPreference);
  const compressedUrl = normalizeId(trackData?.audioFileMP3URL);
  const losslessUrl = normalizeId(trackData?.audioFileWAVURL || trackData?.audioFilePCMURL);

  // A DOWNLOAD outranks the streaming preset, in both directions.
  //
  // The preset says how good the sound should be when we go and get it; a download is a decision
  // already made about these exact bytes, and they are here. Honouring the preset instead would
  // stream a lossless copy past a stored one on every play — the opposite of what downloading is
  // for — and, offline, would ask for a key no download ever stored and find nothing to play.
  //
  // This is also the ONLY place the choice may be made: the decode strategy asks the same question
  // through `isTrackDownloadedSync`, and a variant chosen here that disagreed with the variant found
  // there is precisely how a buffered sink ends up pointed at bytes that are not in the bucket.
  const stored = downloadedTrackVariant(trackData);
  if (stored) {
    return {
      url: stored.variant === 'lossless' ? losslessUrl : compressedUrl,
      variant: stored.variant,
      assetFormat: stored.assetFormat,
    };
  }
  // A listener holds neither URL nor key for either variant, so the
  // variant EXISTS flags are what decide the choice — without them a PCM
  // listener silently falls through to MP3 for every track they do not own.
  // The proxy serves both variants to whoever may hear the release, so nothing
  // here needs the asset itself; `buildStableAudioUrl` names it by variant.
  const compressedAsset =
    compressedUrl || normalizeId(trackData?.audioFileMP3Key) || (trackData?.hasAudioMP3 ? true : null);
  const losslessAsset =
    losslessUrl
    || normalizeId(trackData?.audioFileWAVKey || trackData?.audioFilePCMKey)
    || (trackData?.hasAudioWAV ? true : null);

  if (normalizedPreference === 'pcm') {
    if (losslessAsset) {
      return { url: losslessUrl, variant: 'lossless', assetFormat: 'pcm' };
    }
    if (compressedAsset) {
      return { url: compressedUrl, variant: 'compressed', assetFormat: 'mp3' };
    }
  }

  if (compressedAsset) {
    return { url: compressedUrl, variant: 'compressed', assetFormat: 'mp3' };
  }

  if (losslessAsset) {
    return { url: losslessUrl, variant: 'lossless', assetFormat: 'pcm' };
  }

  return {
    url: null,
    variant: normalizedPreference === 'pcm' ? 'lossless' : 'compressed',
    assetFormat: normalizedPreference,
  };
}

function buildStableAudioUrl({ trackId, releaseVersion, variant }) {
  const base = resolveApiBase();
  // The /tracks/:id/audio proxy only serves PUBLISHED releases (version >= 1). Draft
  // previews carry version 0, which the proxy rejects with 400 — skip the
  // proxy for them so playback falls back to the BE presigned URL instead.
  const numericVersion = Number.parseInt(releaseVersion, 10);
  if (!base || !trackId || !variant || !Number.isInteger(numericVersion) || numericVersion < 1) {
    return null;
  }

  try {
    const url = new URL(`${String(base).replace(/\/+$/, '')}/tracks/${encodeURIComponent(trackId)}/audio`);
    url.searchParams.set('v', String(numericVersion));
    url.searchParams.set('variant', variant);
    return url.toString();
  } catch {
    return null;
  }
}

async function readArrayBufferFromResponse(response, onProgress = null) {
  const startedAt = nowMs();
  const contentLength = response.headers.get('content-length');
  const total = contentLength ? parseInt(contentLength, 10) : 0;

  if (!response.body) {
    const arrayBuffer = await response.arrayBuffer();
    if (onProgress) {
      onProgress(total || arrayBuffer.byteLength, total || arrayBuffer.byteLength, 0);
    }
    const endedAt = nowMs();
    return {
      arrayBuffer,
      loadedBytes: total > 0 ? total : arrayBuffer.byteLength,
      elapsedSeconds: Math.max(0.001, (endedAt - startedAt) / 1000),
    };
  }

  const reader = response.body.getReader();
  const chunks = [];
  let loaded = 0;
  let lastTime = startedAt;
  let lastLoaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    loaded += value.length;

    const currentTime = nowMs();
    const timeDelta = (currentTime - lastTime) / 1000;
    const bytesDelta = loaded - lastLoaded;
    const speed = timeDelta > 0 ? bytesDelta / timeDelta : 0;

    if (onProgress) {
      onProgress(loaded, total, speed);
    }

    if (timeDelta >= 0.5) {
      lastTime = currentTime;
      lastLoaded = loaded;
    }
  }

  let arrayBuffer;
  try {
    arrayBuffer = new Uint8Array(loaded);
  } catch (error) {
    // Assembling the downloaded chunks into one contiguous buffer is the first place a device
    // short on room gives up, and it fails before any decoding is attempted.
    throw tagAudioFailure(error, 'download');
  }
  let position = 0;
  for (const chunk of chunks) {
    arrayBuffer.set(chunk, position);
    position += chunk.length;
  }

  const endedAt = nowMs();
  return {
    arrayBuffer: arrayBuffer.buffer,
    loadedBytes: loaded,
    elapsedSeconds: Math.max(0.001, (endedAt - startedAt) / 1000),
  };
}

/**
 * Mark WHERE the audio pipeline gave out, alongside the `error.status` / `error.url` this module
 * already attaches. Only the stage is recorded, never a guess at the cause: a shared session
 * reacts the same way to a decode that ran out of room and one that hit a bad file — the track
 * cannot play here, and there is no streaming path to quietly fall back to.
 * @param {*} error the thrown value, passed through untouched if it can't carry a property
 * @param {'download'|'decode'} stage
 */
export function tagAudioFailure(error, stage) {
  if (error && typeof error === 'object') {
    error.audioFailure = stage;
  }
  return error;
}

async function fetchArrayBufferFromUrl(url, { onProgress = null, withCacheResponse = false } = {}) {
  // Inside a room the participant is not the owner and holds no signed
  // URL, so the proxy is the only door — and `roomGrantGate` reads the grant
  // from the `x-room-grant` header ALONE. Without it the proxy answers 403 and
  // a room playing a non-public track goes silent. Empty outside a room, so a
  // normal load stays byte-identical.
  const isStable = Boolean(url && isStableAudioUrl(url));
  const response = await fetch(url, {
    credentials: isStable ? 'include' : 'same-origin',
    ...(isStable ? { headers: getRoomGrantHeaders() } : {}),
  });
  if (!response.ok) {
    const error = new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
    error.status = response.status;
    error.url = response.url || url;
    throw error;
  }
  // Cloning a streaming body makes the browser buffer BOTH branches for the whole download, so
  // only pay it when the caller is actually going to write the cache. Asking for it on a fallback
  // URL (or with no logical cache request) doubled the compressed audio in memory for nothing.
  const cacheResponse = withCacheResponse ? response.clone() : null;
  const result = await readArrayBufferFromResponse(response, onProgress);
  if (url && isStableAudioUrl(url)) {
    clearStableAudioUrlTemporarilyUnavailable(url);
  }
  return {
    ...result,
    cacheResponse,
    resolvedUrl: response.url || url,
  };
}

function isStableAudioUrl(url) {
  if (!url) return false;
  const apiBase = resolveApiBase();
  if (!apiBase) return false;
  const normalizedBase = String(apiBase).replace(/\/+$/, '');
  return String(url).startsWith(`${normalizedBase}/tracks/`) && String(url).includes('/audio?');
}

function shouldDisableStableAudioProxyForSession(error) {
  if (error?.name === 'AbortError') {
    return false;
  }
  const status = Number(error?.status);
  return status === 404 || status === 405 || status === 410 || status === 501;
}

export function registerStableAudioProxyFailure(audioSource, error) {
  const stableUrl = audioSource?.stableUrl || null;
  if (!stableUrl) return false;

  if (shouldDisableStableAudioProxyForSession(error)) {
    writeStableAudioUrlRecord(stableUrl, {
      disabledAt: Date.now(),
      status: Number.isFinite(Number(error?.status)) ? Number(error.status) : null,
      noStatusFailures: 0,
      lastFailureAt: Date.now(),
    });
    return true;
  }

  const status = Number(error?.status);
  if (!Number.isFinite(status) || status <= 0) {
    const existing = readStableAudioUrlUnavailableRecord(stableUrl);
    const nextNoStatusFailures = Number(existing?.noStatusFailures) + 1 || 1;
    if (nextNoStatusFailures >= 2) {
      writeStableAudioUrlRecord(stableUrl, {
        disabledAt: Date.now(),
        status: null,
        noStatusFailures: nextNoStatusFailures,
        lastFailureAt: Date.now(),
      });
      return true;
    }
    writeStableAudioUrlRecord(stableUrl, {
      disabledAt: null,
      status: null,
      noStatusFailures: nextNoStatusFailures,
      lastFailureAt: Date.now(),
    });
  }

  return false;
}

async function readFromLogicalAudioCache(cacheRequestUrl, onProgress = null) {
  if (!cacheRequestUrl) return null;
  const cache = await openAudioBucket();
  if (!cache) return null;
  const cachedResponse = await cache.match(cacheRequestUrl);
  if (!cachedResponse || !cachedResponse.ok) {
    return null;
  }

  const result = await readArrayBufferFromResponse(cachedResponse, onProgress);
  // A hit is a play. Without this, "oldest" would mean "first fetched" and the cap would
  // evict the track someone replays most in favour of one they heard once and left.
  await noteAudioCachePlay(cacheRequestUrl, result.loadedBytes);
  return {
    ...result,
    resolvedUrl: cacheRequestUrl,
    fromLogicalCache: true,
  };
}

async function writeToLogicalAudioCache(cacheRequestUrl, response, bytes, generation) {
  if (!cacheRequestUrl || !response?.ok || response.status === 206) {
    return false;
  }

  // The store puts the bytes and ledgers them together — it is the only place that can refuse a
  // write whose identity has gone, or one it would not be able to account for afterwards.
  return writeAudioCacheEntry(cacheRequestUrl, response.clone(), bytes, generation);
}

export function resolvePlaybackAudioSource(trackData, { assetFormatPreference = 'mp3' } = {}) {
  const trackId = normalizeId(trackData?.trackId);
  // A file on this machine. It has no URL, no variant and no cache identity: the supplier opens the
  // file at load time, and nothing here is written to the audio bucket — the promise of the local
  // library is that the bytes are never copied into browser storage.
  const localBytes = getLocalByteSource(trackId);
  if (localBytes) {
    return {
      url: null,
      fallbackUrl: null,
      originalUrl: null,
      stableUrl: null,
      bytes: localBytes,
      variant: 'local',
      assetFormat: null,
      trackId,
      releaseVersion: null,
      identityKey: trackId,
      logicalCacheRequestUrl: null,
    };
  }
  const candidate = resolveVariantCandidate(trackData, assetFormatPreference);
  const releaseVersion = normalizeId(trackData?.version);
  const logicalIdentityKey = buildLogicalCacheIdentity({
    trackId,
    releaseVersion,
    variant: candidate.variant,
    assetFormat: candidate.assetFormat,
  });
  const identityKey = logicalIdentityKey || candidate.url;
  const stableUrl = buildStableAudioUrl({
    trackId,
    releaseVersion,
    variant: candidate.variant,
  });
  // The 5-minute circuit breaker below exists to fall BACK to the
  // signed URL when the proxy misbehaves. A listener no longer receives one, so
  // honouring the breaker there would leave nothing to play at all — one blip
  // would kill a perfectly good track for five minutes, persisted across
  // reloads. With no fallback the proxy is the only door, so keep using it.
  const hasFallback = Boolean(candidate.url);
  // In a room the participant is admitted by the GRANT, and the grant travels in
  // a header. The streaming player reaches audio with an <audio> element, which
  // cannot send one — so preferring the proxy here buys a guaranteed 403 and a
  // persisted breaker entry on every room load, before falling back anyway. When
  // a grant is in play and a signed URL came with the release, take it directly.
  const preferSignedForRoomGrant = hasFallback && Boolean(getRoomGrantId());
  const stableUrlAvailable = Boolean(
    stableUrl
    && !preferSignedForRoomGrant
    && (!hasFallback || !isStableAudioUrlTemporarilyUnavailable(stableUrl)),
  );
  const preferredUrl = stableUrlAvailable ? stableUrl : (candidate.url || null);

  return {
    url: preferredUrl,
    fallbackUrl: stableUrlAvailable && candidate.url ? candidate.url : null,
    originalUrl: candidate.url || null,
    stableUrl: stableUrlAvailable ? stableUrl : null,
    variant: candidate.variant,
    assetFormat: candidate.assetFormat,
    trackId,
    releaseVersion,
    identityKey,
    logicalCacheRequestUrl: toCacheRequestUrl(logicalIdentityKey),
  };
}

/**
 * Read a caller-supplied byte source.
 *
 * The engine's input has always been "a URL it fetches". That is one way to get bytes, not the only
 * one, and the difference matters as soon as bytes can come from somewhere that has no URL at all —
 * a file the user dropped in has neither a platform id nor an address. So the seam takes
 * a RESOLVED source: a Blob, a Response, or a function returning either. Where it came from — a
 * cache, a file handle, a network fetch the caller already made — is the caller's business, and
 * deliberately invisible here, because the alternative is this function growing a branch per source.
 * @param {*} bytes a Blob/Response, or a supplier of one
 * @returns {Promise<{arrayBuffer: ArrayBuffer, loadedBytes: number, elapsedSeconds: number}>}
 */
async function readSuppliedBytes(bytes, onProgress) {
  const resolved = typeof bytes === 'function' ? await bytes() : await bytes;
  if (!resolved) {
    throw new Error('The supplied audio byte source produced nothing.');
  }
  // A Response streams and can report progress; a Blob is already here, so it is wrapped in one
  // rather than special-cased — the progress callback then fires once, honestly, at 100%.
  const response = typeof resolved.arrayBuffer === 'function' && !('body' in resolved)
    ? new Response(resolved)
    : resolved;
  return readArrayBufferFromResponse(response, onProgress);
}

/**
 * @param {object} audioSource from {@link resolvePlaybackAudioSource}, or any object carrying
 *   `bytes` — a Blob/Response or a supplier of one — in place of a `url`.
 */
export async function fetchPrebufferAudioSource(audioSource, { onProgress = null } = {}) {
  // An explicit byte source is checked FIRST and short-circuits everything below. It is already
  // resolved by definition, so a cache lookup or a fetch on top of it would be asking a question
  // the caller has answered.
  if (audioSource?.bytes) {
    const supplied = await readSuppliedBytes(audioSource.bytes, onProgress);
    return { ...supplied, resolvedUrl: audioSource.url || null, fromSuppliedBytes: true };
  }

  if (!audioSource?.url) {
    throw new Error('No audio source URL available.');
  }

  const logicalCacheRequestUrl = audioSource.logicalCacheRequestUrl || null;
  if (logicalCacheRequestUrl) {
    try {
      const cached = await readFromLogicalAudioCache(logicalCacheRequestUrl, onProgress);
      if (cached) {
        return cached;
      }
    } catch (error) {
      console.warn('[AudioAssetSource] Failed to read logical audio cache.', error);
    }
  }

  const candidateUrls = [...new Set([audioSource.url, audioSource.fallbackUrl].filter(Boolean))];
  let lastError = null;
  // Taken BEFORE the fetch: this is the identity the bytes are being requested under. If the
  // bucket is emptied while the download is in flight, the write below is refused rather than
  // restoring audio the viewer is no longer signed in for.
  const cacheGeneration = currentAudioCacheGeneration();

  for (const candidateUrl of candidateUrls) {
    try {
      const wantsCacheWrite = Boolean(logicalCacheRequestUrl) && candidateUrl === audioSource.originalUrl;
      const result = await fetchArrayBufferFromUrl(candidateUrl, {
        onProgress,
        withCacheResponse: wantsCacheWrite,
      });
      if (wantsCacheWrite && result.cacheResponse) {
        try {
          await writeToLogicalAudioCache(
            logicalCacheRequestUrl,
            result.cacheResponse,
            result.loadedBytes,
            cacheGeneration,
          );
        } catch (error) {
          console.warn('[AudioAssetSource] Failed to write logical audio cache.', error);
        }
      }
      return result;
    } catch (error) {
      if (candidateUrl === audioSource.stableUrl) {
        registerStableAudioProxyFailure(audioSource, error);
      }
      lastError = error;
    }
  }

  throw lastError || new Error('Failed to fetch audio source.');
}
